import { Analytics } from '@cobliteam/analytics';
import { useDashboard } from '@cobliteam/dashboard/src/providers/DashboardProvider';
import { useTranslation } from '@cobliteam/i18n';
import { changeUserFleetRequest } from '@cobliteam/store/src/Login';
import Divider from '@cobliteam/ui/components/Divider';
import DropdownMenu from '@cobliteam/ui/components/DropdownMenu';
import Tooltip from '@cobliteam/ui/components/Tooltip';
import Input from '@cobliteam/ui/form/Input';
import Icon from '@cobliteam/ui/icons';
import { cn } from '@cobliteam/ui/utils';
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from '@cobliteam/ui-business/components/AppSidebar/components/Sidebar';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { Avatar, AvatarFallback, AvatarImage } from './AppFleetAvatar';

interface Fleet {
  name: string;
  id: string;
}

interface FleetSwitcherProps {
  fleets: Fleet[];
  currentFleet: Fleet;
}

const getFleetInitials = (fleetName: string) => {
  return fleetName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
};

/**
 * Renderiza o seletor de frota no sidebar, com pesquisa, lista ordenada de frotas e opção de troca.
 *
 * Ao abrir o menu registra um evento de analytics; ao selecionar outra frota despacha a ação de mudança de frota e registra o evento correspondente.
 *
 * @param fleets - Lista de frotas disponíveis exibidas e filtradas; cada item deve conter pelo menos `id` e `name`.
 * @param currentFleet - Frota atualmente selecionada; deve conter `id` e `name`.
 * @returns O elemento React que representa o componente AppFleetSwitcher.
 */
function AppFleetSwitcher({ fleets = [], currentFleet = { name: '', id: '' } }: FleetSwitcherProps) {
  const isDemoMode = process.env.REACT_APP_DEMO_MODE === 'true';
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const dispatch = useDispatch();
  const { runtimeFlags } = useDashboard();
  const { openMobile } = useSidebar();
  const { t } = useTranslation('ui-business');

  const handleChangeFleet = (fleetId: string, fleetName: string) => {
    dispatch(changeUserFleetRequest({ id: fleetId, name: fleetName }));
    Analytics.track('FleetSwitcher - Switch - Fleet', { fleetId, fleetName });
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (isOpen) {
      Analytics.track('FleetSwitcher - Open');
    } else {
      setSearch('');
    }
    setOpen(isOpen);
  };

  const filteredFleets = [...fleets].sort((a, b) => a.name.localeCompare(b.name)).filter((f) => f.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu open={open} onOpenChange={handleOpenChange}>
          <DropdownMenu.Trigger asChild>
            {openMobile ? (
              <SidebarMenuButton
                data-testid='fleet-switcher'
                size='lg'
                className='cursor-pointer data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground'
              >
                <div className='flex aspect-square items-center justify-center rounded-lg text-sidebar-primary-foreground'>
                  <Avatar className='size-10 rounded-md'>
                    {!isDemoMode && <AvatarImage src={`https://assets.cobli.co/fleets/${currentFleet.id}/logo.png`} />}
                    <AvatarFallback className='rounded-md bg-primary-9 font-semibold text-sm text-white'>{getFleetInitials(currentFleet.name)}</AvatarFallback>
                  </Avatar>
                </div>
                <div className='grid flex-1 text-left text-sm leading-tight'>
                  <span className='truncate font-semibold'>{currentFleet.name}</span>
                </div>
                <Icon name='chevrons-up-down' className='ml-auto' />
              </SidebarMenuButton>
            ) : (
              <div className='mt-1' data-testid='fleet-switcher'>
                <div className='flex items-center justify-center text-sidebar-primary-foreground'>
                  <Tooltip content={currentFleet.name} align='center' position='right' delayDuration={400}>
                    <Avatar className='!cursor-pointer size-9 rounded-lg shadow-sm'>
                      {!isDemoMode && <AvatarImage src={`https://assets.cobli.co/fleets/${currentFleet.id}/logo.png`} className='rounded-lg border border-gray-6' />}
                      <AvatarFallback className='rounded-lg bg-primary-9 font-semibold text-white text-xs'>{getFleetInitials(currentFleet.name)}</AvatarFallback>
                    </Avatar>
                  </Tooltip>
                </div>
              </div>
            )}
          </DropdownMenu.Trigger>
          <DropdownMenu.Content
            portal={!openMobile}
            className={cn('-mt-3 w-80 rounded-lg border-gray-6 px-0 pt-2 pb-2 text-sidebar-foreground')}
            alignOffset={10}
            align='start'
            side={openMobile ? 'bottom' : 'right'}
            collisionPadding={{ left: 10 }}
          >
            <section className='flex flex-col pt-2 pr-2 pb-2 pl-4 font-medium text-gray-12 text-sm'>
              <div className='flex items-center justify-between'>
                <div className='flex items-center gap-2'>
                  <span>Lista de Frotas</span>
                </div>
                {!runtimeFlags.noValidGroupOrVehicles && (
                  <Tooltip content='Configurações' portal={false} asChild>
                    <Link to='/config/account' className='cursor-pointer' onClick={() => handleOpenChange(false)}>
                      <Icon name='settings' className='mr-1 size-5 text-gray-10' />
                    </Link>
                  </Tooltip>
                )}
              </div>
            </section>
            <Divider className='mb-2' />
            {fleets.length > 1 && (
              <div className='px-3 pb-2'>
                <Input
                  placeholder={t('fleet_switcher.search_placeholder')}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.stopPropagation()}
                  startIcon={<Icon name='search' className='size-4 text-gray-9' />}
                  endIcon={
                    search ? (
                      <button type='button' aria-label={t('fleet_switcher.clear_search')} onClick={() => setSearch('')} className='flex cursor-pointer items-center justify-center'>
                        <Icon name='x' className='size-4 text-gray-9 hover:text-gray-12' />
                      </button>
                    ) : undefined
                  }
                />
              </div>
            )}
            <SidebarMenu className='px-0'>
              <div
                className={cn(
                  'flex max-h-96 flex-col gap-2 overflow-y-auto',
                  'scrollbar',
                  'scrollbar-thumb-custom',
                  'scrollbar-thin',
                  'scrollbar-thumb-rounded-full scrollbar-track-rounded-full',
                  'scrollbar-thumb-gray-8',
                  'scrollbar-thumb-bg-clip-content',
                  'scrollbar-track-gray-1'
                )}
              >
                {filteredFleets.map((fleet) => {
                  const isActive = currentFleet.id === fleet.id;
                  const fleetLogo = `https://assets.cobli.co/fleets/${fleet.id}/logo.png`;
                  const fleetAvatarFallback = `https://ui-avatars.com/api/?name=${fleet.name}&background=0234E1&color=fff`;

                  return (
                    <SidebarMenuItem key={fleet.id} className='px-2'>
                      <SidebarMenuButton onClick={() => handleChangeFleet(fleet.id, fleet.name)} className='cursor-pointer px-0.5 text-xs capitalize' isActive={isActive}>
                        <Avatar className='size-8 rounded-lg border border-gray-5'>
                          <AvatarImage src={fleetLogo || fleetAvatarFallback} className='rounded-md' />
                          <AvatarFallback className='font-medium text-white'>
                            <img src={fleetAvatarFallback} alt={fleet.name} className='size-full' />
                          </AvatarFallback>
                        </Avatar>
                        <span className='font-medium text-sm'>{fleet.name.toLocaleLowerCase()}</span>
                        {isActive && <Icon name='check' className='ml-auto' />}
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </div>
            </SidebarMenu>
          </DropdownMenu.Content>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}

export default AppFleetSwitcher;
